import { motion } from "framer-motion";
import { X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MovieData } from "@/components/SwipeCard";

interface DiscoverResultModalProps {
  pickedMovie: MovieData | null;
  onClose: () => void;
  onDone?: () => void;
}

const DiscoverResultModal = ({ pickedMovie, onClose, onDone }: DiscoverResultModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDone = () => {
    onClose();
    if (onDone) {
      onDone();
    } else {
      navigate("/");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md flex items-center justify-center px-6"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 text-center shadow-card relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="text-4xl mb-4 space-x-1">
          <span>🎉</span><span>🥳</span><span>🎊</span>
        </div>
        <h2 className="font-display text-2xl font-bold text-foreground mb-2">{t("discover.timesUp")}</h2>
        {pickedMovie ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">{t("discover.pickedForYou")}</p>
            <div className="w-32 aspect-[2/3] rounded-xl overflow-hidden mx-auto mb-3 border border-border">
              <img src={pickedMovie.poster} alt={pickedMovie.title} className="w-full h-full object-cover" />
            </div>
            <h3 className="font-display text-xl font-bold text-gradient-iris">{pickedMovie.title}</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {pickedMovie.year} · {pickedMovie.runtime} min
              {pickedMovie.certification && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-semibold text-muted-foreground border border-border/50 align-middle">
                  {pickedMovie.certification}
                </span>
              )}
            </p>
            {pickedMovie.providers && pickedMovie.providers.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">{t("discover.availableOn")}</p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {pickedMovie.providers.map((p) => {
                    const content = (
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/80 transition-all ${pickedMovie.watch_link ? "hover:bg-secondary hover:ring-1 hover:ring-primary/30 cursor-pointer" : ""}`}>
                        {p.logo_path && <img src={p.logo_path} alt={p.provider_name} className="w-5 h-5 rounded-sm" />}
                        <span className="text-[10px] text-muted-foreground font-medium">{p.provider_name}</span>
                        {pickedMovie.watch_link && <ExternalLink className="w-3 h-3 text-muted-foreground/60" />}
                      </div>
                    );
                    return pickedMovie.watch_link ? (
                      <a key={p.provider_id} href={pickedMovie.watch_link} target="_blank" rel="noopener noreferrer">
                        {content}
                      </a>
                    ) : (
                      <div key={p.provider_id}>{content}</div>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-sm text-foreground mt-4">{t("discover.enjoyShow")}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("discover.noStarredOrBookmarked")}</p>
        )}
        <button
          onClick={handleDone}
          className="mt-6 px-6 py-2.5 rounded-full bg-gradient-iris text-primary-foreground text-sm font-semibold shadow-iris active:scale-95 transition-transform"
        >
          {t("discover.letsWatchIt")}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default DiscoverResultModal;
